import {Component, forwardRef, Input, OnDestroy, OnInit} from '@angular/core';
import {DateAdapter, MAT_DATE_FORMATS, MAT_DATE_LOCALE} from '@angular/material/core';
import {MAT_MOMENT_DATE_ADAPTER_OPTIONS, MomentDateAdapter} from '@angular/material-moment-adapter';
import {ControlValueAccessor, FormControl, NG_VALUE_ACCESSOR} from '@angular/forms';
import {ReplaySubject} from 'rxjs';
import {Moment} from 'moment';
import moment from 'moment';
import {MatDatepicker} from '@angular/material/datepicker';
import {takeUntil} from 'rxjs/operators';
export const MY_FORMATS = {
  parse: {
    dateInput: 'YYYY-MM',
  },
  display: {
    dateInput: 'YYYY-MM',
    monthYearLabel: 'MMM YYYY',
    dateA11yLabel: 'YYYY-MM',
    monthYearA11yLabel: 'MMMM YYYY',
  },
};

@Component({
  selector: 'app-month-picker',
  templateUrl: './month-picker.component.html',
  styleUrls: ['./month-picker.component.scss'],
  providers: [
    {
      provide: DateAdapter,
      useClass: MomentDateAdapter,
      deps: [MAT_DATE_LOCALE, MAT_MOMENT_DATE_ADAPTER_OPTIONS]
    },

    {provide: MAT_DATE_FORMATS, useValue: MY_FORMATS},
    { provide: MAT_DATE_LOCALE, useValue: 'pl-PL' },
    { provide: MAT_MOMENT_DATE_ADAPTER_OPTIONS, useValue: { useUtc: true } },
    {provide: NG_VALUE_ACCESSOR, multi: true, useExisting: forwardRef(() => MonthPickerComponent)}
  ],
})
export class MonthPickerComponent implements ControlValueAccessor, OnInit, OnDestroy {
  @Input() control?: FormControl;
  @Input() required: boolean;
  @Input() minDate: Moment | null = null;
  @Input() maxDate: Moment | null = null;
  @Input() minSalesMonthLabel: string;
  @Input() maxSalesMonthLabel: string;
  @Input() disabledDatePicker: boolean;
  @Input() readonly: boolean;
  @Input() label: string;
  @Input() placeholder: string;

  value?: any = null;
  disabled = false;

  private destroyed$ = new ReplaySubject<boolean>(1);

  onChange: any = () => { };
  onTouched: any = () => { };

  ngOnInit(): void {
    if (!this.control) {
      this.control = new FormControl(moment());
    }

    this.control.valueChanges
      .pipe(takeUntil(this.destroyed$))
      .subscribe(data => {
        if (data) {
          this.value = data;
          this.onChange(data);
        }
      });
  }

  ngOnDestroy(): void {
    this.destroyed$.next(true);
    this.destroyed$.complete();
  }

  registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    if (!this.control) {
      return;
    }

    if (isDisabled) {
      this.control.disable();
    } else {
      this.control.enable();
    }

    this.disabled = isDisabled;
  }

  writeValue(value: any): void {
    if (!this.control) {
      return;
    }

    this.control.setValue(value);
    this.value = value;
  }

  chosenYearHandler(normalizedYear: Moment): void {
    const ctrlValue = this.getControlValue();

    ctrlValue.year(normalizedYear.year());

    const normalizedValue = this.normalizeToAllowedRange(ctrlValue);
    this.control?.setValue(normalizedValue);
  }

  chosenMonthHandler(
    normalizedMonth: Moment,
    datepicker: MatDatepicker<Moment>,
  ): void {
    const ctrlValue = this.getControlValue();

    ctrlValue.month(normalizedMonth.month());
    ctrlValue.startOf('month');

    if (!this.isInAllowedRange(ctrlValue)) {
      return;
    }

    this.control?.setValue(ctrlValue);
    datepicker.close();
  }

  private normalizeToAllowedRange(value: Moment): Moment {
    if (this.minDate && value.isBefore(this.minDate, 'month')) {
      return this.minDate.clone().startOf('month');
    }

    if (this.maxDate && value.isAfter(this.maxDate, 'month')) {
      return this.maxDate.clone().startOf('month');
    }

    return value.startOf('month');
  }

  private isInAllowedRange(value: Moment): boolean {
    if (this.minDate && value.isBefore(this.minDate, 'month')) {
      return false;
    }

    if (this.maxDate && value.isAfter(this.maxDate, 'month')) {
      return false;
    }

    return true;
  }

  private getControlValue(): Moment {
    const value = this.control?.value;

    if (moment.isMoment(value)) {
      return value.clone();
    }

    if (value) {
      const parsedValue = moment(value, ['YYYY-MM', moment.ISO_8601], true);

      if (parsedValue.isValid()) {
        return parsedValue.startOf('month');
      }
    }

    return moment().startOf('month');
  }
}
